/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, DoCheck, Inject, Input, OnChanges, SimpleChanges, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, firstValueFrom, map, of, switchMap} from 'rxjs';
import {AdServerConfig} from '../config/api-config';
import {AdTagReplacers} from './ad-tag-replacers';
import {calculateNetCpm} from '../line-item/fees-calculator';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {AdUnitCreativeTotalsPreviewModalComponent} from './ad-unit-creative-totals-preview-modal.component';
import * as _ from 'lodash';
import template from './ad-unit-analysis-page.component.html';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {deferSignalStateWrite} from '../common/util/template-state';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {AdUnitApiService} from './ad-unit-api.service';
import {AdUnitDiagnosticsService} from './ad-unit-diagnostics.service';

type AnalysisTab = 'adUnit' | 'external' | 'tag' | 'reports' | 'traffic' | 'location' | 'availability' | 'diagnostics' | 'explore';

interface DisplayItem {
  bold?: string;
  text?: string;
  label?: string;
  subItems?: string[];
}

@Component({
  standalone: false,
  selector: 'adn-ad-unit-analysis-page',
  template
})
export class AdUnitAnalysisPageComponent implements DoCheck, OnChanges {
  @Input() model: any;
  @Input() state: SignalAdminResourcePageState<any>;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  notesSaveTarget: any;
  activeTab: AnalysisTab = 'adUnit';

  networkTagId: string;
  chartCurrency: any;
  canSeeReports = false;
  isPureMarketplace = false;

  idType: 'CUSTOM' | 'MAILCHIMP' = 'CUSTOM';
  defaultMailchimp = '*|CAMPAIGN_UID|**|UNIQID|*';
  uniqueId = '';
  private readonly emailCodeState = signal('');
  private readonly diagRunningState = signal(false);
  private readonly diagResultsState = signal(false);
  private readonly floorPriceState = signal<any>(null);
  private readonly winningResultsState = signal(false);
  private readonly competingResultsState = signal(false);
  private readonly selectedResultsState = signal(false);
  private readonly eligibleResultsState = signal(false);
  private readonly ineligibleResultsState = signal(false);
  private readonly unsuitableResultsState = signal(false);

  get emailCode(): string { return this.emailCodeState(); }
  set emailCode(value: string) { this.emailCodeState.set(value); }
  get diagRunning(): boolean { return this.diagRunningState(); }
  set diagRunning(value: boolean) { this.diagRunningState.set(value); }
  get diagResults(): boolean { return this.diagResultsState(); }
  set diagResults(value: boolean) { this.diagResultsState.set(value); }
  get floorPrice(): any { return this.floorPriceState(); }
  set floorPrice(value: any) { this.floorPriceState.set(value); }
  get winningResults(): boolean { return this.winningResultsState(); }
  set winningResults(value: boolean) { this.winningResultsState.set(value); }
  get competingResults(): boolean { return this.competingResultsState(); }
  set competingResults(value: boolean) { this.competingResultsState.set(value); }
  get selectedResults(): boolean { return this.selectedResultsState(); }
  set selectedResults(value: boolean) { this.selectedResultsState.set(value); }
  get eligibleResults(): boolean { return this.eligibleResultsState(); }
  set eligibleResults(value: boolean) { this.eligibleResultsState.set(value); }
  get ineligibleResults(): boolean { return this.ineligibleResultsState(); }
  set ineligibleResults(value: boolean) { this.ineligibleResultsState.set(value); }
  get unsuitableResults(): boolean { return this.unsuitableResultsState(); }
  set unsuitableResults(value: boolean) { this.unsuitableResultsState.set(value); }
  vast2 = '';
  vast3 = '';
  vast4 = '';
  vast4_1 = '';
  vast4_2 = '';
  native = '';
  whitelabel = false;
  whitelabelDomain = '';
  selTag: {name: string; order?: number} = {name: 'COM'};

  showingChart: string | null = null;
  showingTraffic = false;
  showingLocation = false;
  showingReach = false;
  showingExplorer = false;
  visibleReports = false;

  showTargeting = false;
  diag: any;
  diagLineItemId: string | null = null;
  error: string | null = null;
  errorInfo: string | null = null;
  country: string;
  city: string;
  temperature: any;
  temperatureLocation: string;
  entryCost: string;
  politicalApprovalRequired = false;
  showLocationInfo = false;

  winning: any[] = [];
  competing: any[] = [];
  selected: any[] = [];
  eligible: any[] = [];
  ineligible: any[] = [];
  unsuitable: any[] = [];
  totalLineItems = 0;

  private totalsData: any[];
  private creativeSorting: any;
  private lastLoadedTagsModelId: string | undefined;
  private lastSeenEffectiveIsNew: boolean | undefined;

  private readonly ogEmail = '<a href="https://delivery.adnuntius.com/c?network=[[TAG_ID]]&auId=[[AD_UNIT]]&autor=true&iid=[THIS_MUST_BE_REPLACED]">\n    <img src="https://delivery.adnuntius.com/i?network=[[TAG_ID]]&auId=[[AD_UNIT]]&format=image&iid=[THIS_MUST_BE_REPLACED]"[[AD_UNIT_WIDTH]][[AD_UNIT_HEIGHT]] />\n</a>\n';
  private readonly ogVast: string;
  private readonly ogNative: string;

  readonly callbackHook = (): void => {
    this.doPreviews();
  };

  readonly statsHook = (totals: any, sorting: any): void => {
    if (totals) {
      this.totalsData = _.cloneDeep(totals);
    }
    this.creativeSorting = _.cloneDeep(sorting);
  };

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly diagnostics: AdUnitDiagnosticsService,
    private readonly api: AdUnitApiService,
    private readonly angularModalService: AngularModalService,
    private readonly appLocale: AppLocaleService,
    private readonly destroyRef: DestroyRef
  ) {
    this.networkTagId = LocalNetworkProfile.get('tagId');
    this.ogVast = AdServerConfig.obtain().getUri('i') + '?network=[[TAG_ID]]&auId=[[AD_UNIT]]&tt=[[VAST_VERSION]]';
    this.ogNative = AdServerConfig.obtain().getUri('i') + '?network=[[TAG_ID]]&auId=[[AD_UNIT]]&tt=native';
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  get hasExternalAdUnits(): boolean {
    return (this.model?.externalAdUnits?.length || 0) > 0;
  }

  get tagNavItems(): any[] {
    return _.filter(this.model?.tags?.tags || [], (tag) => tag.name !== 'FIF');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
      this.lastSeenEffectiveIsNew = this.effectiveIsNew;
      if (this.effectiveIsNew) {
        this.lastLoadedTagsModelId = undefined;
      }
    }
    if (changes.model) {
      this.notesSaveTarget = this.state ? this.state.notesSaveTarget() : this.model;
      this.chartCurrency = _.get(this.model, ['floorPrice', 'currency']);
      this.initDiag();
      this.loadTagsIfNeeded();
    }
  }

  ngDoCheck(): void {
    if (this.lastSeenEffectiveIsNew !== this.effectiveIsNew) {
      if (this.lastSeenEffectiveIsNew === true && this.effectiveIsNew === false) {
        this.lastLoadedTagsModelId = undefined;
      }
      this.lastSeenEffectiveIsNew = this.effectiveIsNew;
    }
    this.loadTagsIfNeeded();
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'external') {
      this.showingChart = 'EXTERNAL_AD_UNIT';
    } else if (tab === 'reports') {
      this.visibleReports = true;
    } else if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    } else if (tab === 'availability') {
      this.showingReach = true;
    } else if (tab === 'explore') {
      this.showingExplorer = true;
    }
  }

  changeValue(): void {
    this.updateTagCode();
  }

  selectTagName(name: string, event?: Event): void {
    event?.preventDefault();
    this.selTag.name = name;
  }

  addKv(): void {
    this.diag.kv.push({k: '', v: ''});
  }

  removeKv(index: number): void {
    _.pullAt(this.diag.kv, index);
  }

  searchLineItems(term: string): Promise<any[]> {
    if (!term) {
      return Promise.resolve([]);
    }
    return firstValueFrom(this.api.searchLineItems(term));
  }

  doPreviews(): void {
    this.angularModalService.show(AdUnitCreativeTotalsPreviewModalComponent, {
      class: 'modal-lg',
      initialState: {
        totalsData: this.totalsData,
        creativeSorting: this.creativeSorting
      }
    });
  }

  runDiag(): void {
    this.diagRunning = true;
    this.diagResults = false;
    this.error = null;
    this.errorInfo = null;
    this.diagLineItemId = _.trim(this.diag.dxLineItemId) || null;
    this.resolveFloorPrice();

    this.diagnostics.run(this.diag).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => {
        const lineItemParticipants = _.isArray(data.lineItemParticipants) ? data.lineItemParticipants : [];
        _.forEach(lineItemParticipants, (l) => {
          this.processLineItemParticipant(l);
        });

        this.diagRunning = false;
        this.diagResults = true;
        this.country = data.country;
        this.city = data.city;
        this.temperature = data.temperature;
        this.temperatureLocation = data.temperatureLocation;
        this.entryCost = this.normaliseEntryCost(data.entryCost);
        this.politicalApprovalRequired = data.politicalApprovalRequired === true;

        this.doQueries('winning', 30, lineItemParticipants);
        this.doQueries('selected', 30, lineItemParticipants);
        this.doQueries('selected', 30, lineItemParticipants, true);
        this.doQueries('eligible', 30, lineItemParticipants);
        this.doQueries('unsuitable', 30, lineItemParticipants);
        this.doQueries('ineligible', 10, lineItemParticipants);

        const unsuitable = this.unsuitable || [];
        const ineligible = this.ineligible || [];
        this.ineligible = ineligible.concat(unsuitable);
        this.totalLineItems = lineItemParticipants.length;
      },
      error: (response) => {
        this.errorInfo = response.info;
        this.error = response.clientError || response.statusText || '';
        this.diagRunning = false;
      }
    });
  }

  private loadTagsIfNeeded(): void {
    if (this.effectiveIsNew) {
      return;
    }
    const modelId = this.model?.id;
    if (!modelId) {
      return;
    }
    if (this.lastLoadedTagsModelId === modelId) {
      return;
    }
    this.lastLoadedTagsModelId = modelId;

    if (this.model.tags?.auId) {
      this.initTags();
      this.updateTagCode();
      return;
    }
    this.api.getTags(modelId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (tags) => {
        this.model.tags = tags;
        this.initTags();
        this.updateTagCode();
      },
      error: () => {
        if (this.lastLoadedTagsModelId === modelId) {
          this.lastLoadedTagsModelId = undefined;
        }
      }
    });
  }

  private initDiag(): void {
    const auId = this.model?.tags?.auId;
    this.diag = {
      auId: auId,
      tzo: new Date().getTimezoneOffset(),
      kv: [{k: '', v: ''}],
      requestType: 'NORMAL'
    };
  }

  private initTags(): void {
    this.updateSelTags();
    if (_.get(this.model, ['tags', 'tags'])) {
      const tagNames = _.map(this.model.tags.tags, 'name');
      if (!tagNames.includes('email')) {
        this.model.tags.tags.push({name: 'email'});
      }
      if (!tagNames.includes('vast')) {
        this.model.tags.tags.push({name: 'vast'});
      }
      if (!tagNames.includes('native')) {
        this.model.tags.tags.push({name: 'native'});
      }
    }
    this.whitelabelDomain = AdTagReplacers.getNetworkWhitelabelDomain(this.LocalNetworkProfile.getNetworkTag());
    this.whitelabel = !!this.whitelabelDomain;
    this.initDiag();
    this.updateTagCode();
  }

  private updateSelTags(): void {
    this.selTag = {name: 'COM'};
    _.forEach(this.model?.tags?.tags, (tag) => {
      if (tag.name === 'COM') {
        tag.order = 1;
        return;
      }
      tag.order = 2;
    });
  }

  private updateTagCode(): void {
    this.emailCode = '';
    const auTagId = _.get(this.model, ['tags', 'auId']);
    if (!auTagId) {
      return;
    }

    let adUnitString = this.ogEmail.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId);
    const widthReplacer = this.model.width ? (' width="' + this.model.width + '"') : '';
    const heightReplacer = this.model.height ? (' height="' + this.model.height + '"') : '';
    adUnitString = adUnitString.replace('[[AD_UNIT_WIDTH]]', widthReplacer).replace('[[AD_UNIT_HEIGHT]]', heightReplacer);

    if (this.idType === 'MAILCHIMP') {
      adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.defaultMailchimp);
    } else if (this.uniqueId) {
      adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.uniqueId);
    }
    this.emailCode = this.emailCode + adUnitString;

    this.vast2 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast2');
    this.vast3 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast3');
    this.vast4 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4');
    this.vast4_1 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4-1');
    this.vast4_2 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4-2');
    this.native = this.ogNative.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId);

    if (this.whitelabel && this.whitelabelDomain) {
      this.emailCode = AdTagReplacers.replace(this.emailCode, this.whitelabelDomain, this.networkTagId);
      this.vast2 = AdTagReplacers.replace(this.vast2, this.whitelabelDomain, this.networkTagId);
      this.vast3 = AdTagReplacers.replace(this.vast3, this.whitelabelDomain, this.networkTagId);
      this.vast4 = AdTagReplacers.replace(this.vast4, this.whitelabelDomain, this.networkTagId);
      this.vast4_1 = AdTagReplacers.replace(this.vast4_1, this.whitelabelDomain, this.networkTagId);
      this.vast4_2 = AdTagReplacers.replace(this.vast4_2, this.whitelabelDomain, this.networkTagId);
      this.native = AdTagReplacers.replace(this.native, this.whitelabelDomain, this.networkTagId);
    }
  }

  private resolveFloorPrice(): void {
    this.floorPrice = null;
    if (this.model?.floorPrice?.amount) {
      this.floorPrice = this.model.floorPrice;
    } else if (this.model?.siteRuleset?.id) {
      this.loadFloorPriceFromRuleset(this.model.siteRuleset.id);
    } else if (this.model?.site?.id) {
      this.api.getSite(this.model.site.id).pipe(
        switchMap((site) => {
          if (site.siteRuleset?.id) {
            return this.api.getRuleset(site.siteRuleset.id);
          }
          if (!site.earningsAccount?.id) {
            return of(null);
          }
          return this.api.getEarningsAccount(site.earningsAccount.id).pipe(
            switchMap((earningsAccount) => earningsAccount.siteRuleset?.id
              ? this.api.getRuleset(earningsAccount.siteRuleset.id)
              : of(null))
          );
        }),
        map((ruleset) => ruleset?.floorPrice || null),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((floorPrice) => {
        this.floorPrice = floorPrice;
      });
    }
  }

  private loadFloorPriceFromRuleset(rulesetId: string): void {
    this.api.getRuleset(rulesetId).pipe(
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((ruleset) => {
      this.floorPrice = ruleset.floorPrice;
    });
  }

  private processLineItemParticipant(l: any): void {
    const joinKeys = ['unmatchedAds', 'matchedAds', 'winningAds', 'competingExternalAds'];
    _.forEach(joinKeys, (k) => {
      const arr = _.isArray(l[k]) ? l[k] : [];
      l[k + 'Count'] = arr.length;
      l[k] = arr.join(', ');
    });
    l.lossReasons = _.isArray(l.lossReasons) ? l.lossReasons : [];
    l.lossReasonsCount = l.lossReasons.length;

    const matched = _.map(l.matchedExpressions || [], (t: string) => this.buildExpressionItem(t, false));
    const negatedMatched = _.map(l.negatedMatchedExpressions || [], (t: string) => this.buildExpressionItem(t, true));
    l.matchedDisplay = matched.concat(negatedMatched);

    const unmatchedTypes = this.buildUnmatchedTypeItems(l.unmatchedExpressionTypes);
    const negatedUnmatched = _.map(l.negatedUnmatchedExpressions || [], (t: string) => this.buildExpressionItem(t, true));
    const unmatchedFilters = this.buildUnmatchedFilterItems(l.unmatchedFilters);
    l.unmatchedDisplay = unmatchedTypes.concat(negatedUnmatched).concat(unmatchedFilters);

    l.blocklistDiagnostics = _.isArray(l.blocklistDiagnostics) ? l.blocklistDiagnostics : [];
    l.blocklistDiagnosticsCount = l.blocklistDiagnostics.length;
    l.floorDiagnosticDetails = this.normaliseFloorDiagnostics(l.floorDiagnostics);
    l.floorDiagnostics = _.map(l.floorDiagnosticDetails, 'summary');
    l.floorDiagnosticsCount = l.floorDiagnostics.length;
    l.hasMoreDiagnosticInfo = l.floorDiagnosticsCount > 0 || l.blocklistDiagnosticsCount > 0;
  }

  private doQueries(key: string, max: number, lineItemParticipants: any[], external?: boolean): void {
    const newKey = external ? 'competing' : key;
    this[newKey + 'Results'] = false;
    this[newKey] = _.filter(lineItemParticipants, (l) => {
      const matchedStage = l.reachedStage === key.toUpperCase();
      if (matchedStage && key.toUpperCase() !== 'SELECTED') {
        return matchedStage;
      }
      if (external) {
        return matchedStage && l.competingExternalAdsCount > 0;
      }
      return matchedStage && l.competingExternalAdsCount < 1;
    });
    const ids = _.map(this[newKey], 'externalId');
    if (ids.length === 0) {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this[newKey + 'Results'] = true;
      });
      return;
    }
    this.api.getLineItems(ids.slice(0, max)).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => deferSignalStateWrite(() => {
        const lineItems = data.results;
        _.forEach(this[newKey], (l) => {
          const li = _.find(lineItems, ['id', l.externalId]);
          if (li) {
            l.lineItemName = _.get(li, 'name');
            l.type = li.type;
            if (li.type === 'SPONSORSHIP') {
              l.sponsorshipPercentage = li.sponsorshipPercentage;
            } else {
              l.smoothed = li.smoothed;
            }
            l.tier = li.tier;
            l.bidSpecification = li.bidSpecification;
            l.objectives = li.objectives;
            if (l.bidSpecification?.cpm) {
              const fees = (l.bidSpecification.cpmFees || []);
              if (l.bidSpecification.cpmTechFee) {
                fees.push(l.bidSpecification.cpmTechFee);
              }
              l.bidSpecification.cpm = calculateNetCpm(fees, l.bidSpecification.cpm);
            }
          }
        });
        this[newKey + 'Results'] = true;
      }),
      error: () => deferSignalStateWrite(() => this[newKey + 'Results'] = true)
    });
  }

  private cleanDiagnosticText(value: any): string {
    return _.isNil(value) ? '' : _.trim(String(value));
  }

  private formatMoneyAsCpm(value: any): string {
    const cleanedValue = this.cleanDiagnosticText(value);
    if (!cleanedValue) {
      return 'Unknown';
    }
    if (/CPM$/i.test(cleanedValue)) {
      return cleanedValue;
    }
    const moneyMatch = cleanedValue.match(/^([A-Z]{3})\s+(-?\d+(?:\.\d+)?)$/);
    if (moneyMatch) {
      const cpmValue = Number(moneyMatch[2]) * 1000;
      if (!Number.isNaN(cpmValue)) {
        return moneyMatch[1] + ' ' + cpmValue.toLocaleString(this.appLocale.getLocaleId(), {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }) + ' CPM';
      }
    }
    return cleanedValue;
  }

  private normaliseFloorSourceLabel(value: any): string {
    const cleanedValue = this.cleanDiagnosticText(value);
    if (!cleanedValue) {
      return 'floor';
    }
    return cleanedValue
      .replace(/^resolved\s+/i, '')
      .replace(/^ad unit floor$/i, 'ad unit floor')
      .replace(/^inventory ruleset floor(?:\s*\(rulesetId=.*\))?$/i, 'inventory floor')
      .replace(/^resolved inventory floor$/i, 'inventory floor');
  }

  private normaliseFloorDiagnostics(value: any): Array<{summary: string; details: string[]}> {
    const entries = _.isArray(value) ? value : [];
    return _.map(entries, (entry) => {
      let parsedEntry = entry;
      if (_.isString(entry)) {
        try {
          parsedEntry = entry.charAt(0) === '{' ? JSON.parse(entry) : null;
        } catch (e) {
          parsedEntry = null;
        }
      }

      if (!parsedEntry || !_.isPlainObject(parsedEntry)) {
        return {summary: this.cleanDiagnosticText(entry), details: []};
      }

      const creative = this.cleanDiagnosticText(parsedEntry.creative || 'unknown creative');
      const status = (parsedEntry.status || 'unknown').toLowerCase();
      const bid = this.formatMoneyAsCpm(parsedEntry.bid || 'Unknown');
      const resolvedFloor = this.formatMoneyAsCpm(parsedEntry.resolvedFloor || 'Unknown');
      const failedComponents = _.isArray(parsedEntry.failedComponents) ? parsedEntry.failedComponents : [];
      const applied = _.isArray(parsedEntry.applied) ? parsedEntry.applied.map((component: any) => this.cleanDiagnosticText(component)) : [];

      let summary: string;
      if (status === 'blocked' && failedComponents.length > 0) {
        const firstFailedComponent = failedComponents[0];
        const source = this.normaliseFloorSourceLabel(firstFailedComponent.source || firstFailedComponent.type || 'floor');
        const floor = this.formatMoneyAsCpm(firstFailedComponent.floor || resolvedFloor);
        summary = 'Creative ' + creative + ' blocked by ' + source + ': bid ' + bid + ', floor ' + floor + '.';
      } else if (status === 'blocked') {
        summary = 'Creative ' + creative + ' blocked by floor: bid ' + bid + ', floor ' + resolvedFloor + '.';
      } else if (status === 'bypassed') {
        summary = 'Creative ' + creative + ' bypassed floor checks: bid ' + bid + ', floor ' + resolvedFloor + '.';
      } else {
        summary = 'Creative ' + creative + ' passed floor checks: bid ' + bid + ', floor ' + resolvedFloor + '.';
      }

      const details: string[] = [];
      if (failedComponents.length > 1) {
        details.push('Failed components:');
      }
      _.forEach(failedComponents, (component) => {
        if (component && component.type) {
          const type = this.cleanDiagnosticText(component.type);
          const floor = this.formatMoneyAsCpm(component.floor || 'Unknown');
          const source = this.normaliseFloorSourceLabel(component.source || '');
          details.push(_.startCase(type) + ': ' + floor + (source ? ' (' + source + ')' : ''));
        }
      });
      if (applied.length > 0) {
        details.push('Applied: ' + applied.join(', '));
      }

      return {summary: summary, details: details};
    });
  }

  private normaliseEntryCost(value: any): string {
    if (_.isNil(value) || value === '') {
      return 'Unknown';
    }
    if (_.isNumber(value)) {
      return Number(value * 1000).toLocaleString(this.appLocale.getLocaleId(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }) + ' CPM';
    }
    return this.formatMoneyAsCpm(value);
  }

  private buildExpressionItem(text: string, negated: boolean): DisplayItem {
    const cleaned = this.cleanDiagnosticText(text);
    if (!negated) {
      return {text: cleaned};
    }
    return {bold: 'NOT', text: cleaned.replace(/^NOT\s+/, '')};
  }

  private buildUnmatchedTypeItems(value: any): DisplayItem[] {
    if (!_.isPlainObject(value)) {
      return [];
    }
    return _.map(value, (count, key) => {
      return {text: key + ' (x' + count + ')'};
    });
  }

  private buildUnmatchedFilterItems(value: any): DisplayItem[] {
    if (!_.isPlainObject(value)) {
      return [];
    }
    return _.map(value, (values, key) => {
      return {label: key + ':', subItems: _.isArray(values) ? values : []};
    });
  }
}
